import Select from "@/components/Select";
import React, { useMemo } from "react";
import { Comparator, comparatorDefs, SerializedCondition } from "../types";
import ReactSelect, { SingleValue } from "react-select";
import { languageOptions } from "@/constants/languages";
import RegexPatternInput from "./RegexPatternInput";

export type LanguageCondition = {
  language: string;
  comparator: Comparator;
};

export function LanguageEditor({
  condition,
  onUpdate
}: {
  condition: SerializedCondition;
  onUpdate: (condition: SerializedCondition) => void;
}) {
  const languageCondition = JSON.parse(
    condition.data || "{}"
  ) as LanguageCondition;

  const languageSelectOptions = useMemo(() => {
    return Object.keys(languageOptions).map((key) => ({
      value: key,
      label: key
    }));
  }, []);

  const selectedLanguage = useMemo(() => {
    if (!languageCondition.language) return null;
    return {
      value: languageCondition.language,
      label: languageCondition.language
    };
  }, [languageCondition.language]);

  function update(data: LanguageCondition) {
    onUpdate({ ...condition, data: JSON.stringify(data) });
  }

  function updateLanguage(
    selectedOption: SingleValue<{ label: string; value: string }>
  ) {
    update({
      ...languageCondition,
      language: selectedOption?.value || ""
    });
  }

  const validComparatorOptions: Record<string, string> = {
    equals: comparatorDefs.equals,
    notEquals: comparatorDefs.notEquals,
    exists: comparatorDefs.exists,
    notExists: comparatorDefs.notExists,
    regex: comparatorDefs.regex,
    notRegex: comparatorDefs.notRegex
  };

  const isRegex =
    languageCondition.comparator === "regex" ||
    languageCondition.comparator === "notRegex";
  const isExistence =
    languageCondition.comparator === "exists" ||
    languageCondition.comparator === "notExists";

  return (
    <>
      <Select
        options={validComparatorOptions}
        value={languageCondition.comparator}
        onChange={(v) => update({ ...languageCondition, comparator: v })}
      />
      {!isExistence && (
        isRegex ? (
          <RegexPatternInput
            value={languageCondition.language || ""}
            onChange={(v) => update({ ...languageCondition, language: v })}
            placeholder="e.g. ^(fr|fr-FR|fr-CA)"
            hint="Matches the browser's language code (e.g. en-US, fr, zh-TW). Case-insensitive."
            examples={[
              "^(fr|fr-FR|fr-CA)",
              "^zh(-CN|-TW|-HK)?",
              "^(es|pt)(-.*)?$"
            ]}
          />
        ) : (
          <ReactSelect
            name="language"
            options={languageSelectOptions}
            value={selectedLanguage}
            className="basic-select w-full"
            classNamePrefix="select"
            onChange={(e) => updateLanguage(e)}
            placeholder="Select language..."
            isClearable
            styles={{
              control: (base) => ({
                ...base,
                minHeight: "40px",
                height: "40px",
                borderRadius: "0.375rem",
                borderColor: "#d1d5db",
                cursor: "pointer"
              }),
              valueContainer: (base) => ({
                ...base,
                height: "40px",
                padding: "0 8px"
              }),
              input: (base) => ({
                ...base,
                margin: "0",
                padding: "0"
              }),
              indicatorsContainer: (base) => ({
                ...base,
                height: "40px"
              })
            }}
          />
        )
      )}
    </>
  );
}
